import { useRef, useCallback } from 'react';
import { toast } from 'sonner';
import type { DatabaseLoad } from '@/lib/loadSearch';
import { getSoundEnabled, getSoundVolume } from '@/lib/soundSettings';

/** Max tracked IDs before FIFO eviction */
const MAX_ALERTED_IDS = 500;

/** Cached Audio instance for alert sound */
let alertAudio: HTMLAudioElement | null = null;
function getAlertAudio(): HTMLAudioElement {
  if (!alertAudio) {
    alertAudio = new Audio('/sounds/alert-ping.mp3');
  }
  alertAudio.volume = getSoundVolume();
  return alertAudio;
}

export function playAlertSound() {
  if (!getSoundEnabled()) return;
  try {
    const audio = getAlertAudio();
    audio.currentTime = 0;
    audio.play().catch(() => { /* autoplay blocked */ });
  } catch {
    // ignore
  }
}

function formatRate(load: DatabaseLoad): string {
  if (!load.rate_amount) return '';
  return ` · $${load.rate_amount.toLocaleString()}`;
}

function formatCompany(load: DatabaseLoad): string {
  // company name isn't on the load row directly; use contact_name as proxy
  if (load.contact_name) return ` · ${load.contact_name}`;
  return '';
}

export function useSearchAlerts() {
  const alertedIds = useRef<string[]>([]);
  const permissionRef = useRef<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  /** Request browser notification permission (call once when user enables alerts) */
  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    if (permissionRef.current === 'granted') return;
    try {
      const result = await Notification.requestPermission();
      permissionRef.current = result;
    } catch {
      // ignore
    }
  }, []);

  /** Check if a load has already been alerted */
  const hasAlerted = useCallback((loadId: string): boolean => {
    return alertedIds.current.includes(loadId);
  }, []);

  /** Mark a load as alerted */
  const markAlerted = useCallback((loadId: string) => {
    if (alertedIds.current.length >= MAX_ALERTED_IDS) {
      alertedIds.current = alertedIds.current.slice(-Math.floor(MAX_ALERTED_IDS / 2));
    }
    alertedIds.current.push(loadId);
  }, []);

  /** Fire in-app toast + browser notification for a new matching load */
  const fireAlert = useCallback((load: DatabaseLoad) => {
    if (hasAlerted(load.id)) return;
    markAlerted(load.id);

    const title = `New Load: ${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`;
    const body = `${load.equipment_type}${formatRate(load)}${formatCompany(load)}`;

    // Play alert sound
    playAlertSound();

    // In-app toast is handled by the caller (e.g. handleNewLoad) with a "View Load" action button

    // Browser notification
    if (permissionRef.current === 'granted' && typeof Notification !== 'undefined') {
      try {
        new Notification(title, {
          body,
          icon: '/favicon.png',
          tag: `load-alert-${load.id}`,
        });
      } catch {
        // ignore
      }
    }
  }, [hasAlerted, markAlerted]);

  /** Clear alerted IDs (e.g. when tab changes or search re-runs) */
  const clearAlerted = useCallback(() => {
    alertedIds.current = [];
  }, []);

  return { fireAlert, requestPermission, clearAlerted };
}
